import React, { useEffect, useImperativeHandle, useState, forwardRef } from "react";
import { Table, theme } from "antd";

import type { BeneficiaryItem, Investment , Frequency } from "../types";

import useAmountByKey from "./helpers/useAmountByKey";
import useColumns from "./helpers/useColumns";
import useItems from "./helpers/useItems";
import BeneficiariesTableSummary from "./BeneficiariesTableSummary";

type Props = {
  investments: Investment[];
  shouldRoundCents: boolean;
  distributionFrequency: Frequency;
}

export type BeneficiariesTableHandle = {
  getSelectedDistributions: () => { amount: number; [key: string]: unknown }[];
  getTotalAmount: () => number;
}

const BeneficiariesTable = forwardRef<BeneficiariesTableHandle, Props>(({
  investments,
  shouldRoundCents,
  distributionFrequency,
}, ref) => {
  const { token } = theme.useToken();

  const [ selectedRowKeys, setSelectedRowKeys ] = useState<React.Key[]>([]);

  const items = useItems(investments);
  const { amountByKey, customAmountByKey, setCustomAmountByKey } = useAmountByKey(items, distributionFrequency, shouldRoundCents);

  const columns = useColumns(amountByKey, customAmountByKey, setCustomAmountByKey, selectedRowKeys);

  useImperativeHandle(ref, () => ({
    getSelectedDistributions: () =>
      items
        .filter(({ key }) => selectedRowKeys.includes(key))
        .map(item => ({ ...item, amount: amountByKey[item.key] })),
    getTotalAmount: () =>
      selectedRowKeys
        .map(key => amountByKey[key as string])
        .reduce((sum, value) => sum + (Number(value) || 0), 0),
  }), [ items, selectedRowKeys, amountByKey ]);

  useEffect(() => {
    setSelectedRowKeys(items.map(({ key }) => key));
  }, [ items ]);

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  const tableProps = {
    size: "small" as const,
    rowKey: "key",
    bordered: true,
    dataSource: items,
    pagination: false as const,
    columns,
    rowSelection,
    onRow: ({ key }: { key: string }) => ({
      style: selectedRowKeys.includes(key)
        ? undefined
        : {
          backgroundColor: token.colorFillAlter,
          color: token.colorTextDisabled,
        },
    }),
    summary: () => (
      <BeneficiariesTableSummary
        amountByKey={amountByKey}
        selectedRowKeys={selectedRowKeys as string[]}
      />
    ),
  };

  return (
    <Table<BeneficiaryItem> {...tableProps} />
  );
});

BeneficiariesTable.displayName = "BeneficiariesTable";

export default BeneficiariesTable;
